import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowUp, ArrowDown, Upload, Pencil } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const photoCategories = [
  { value: 'plats', label: 'Plats' },
  { value: 'terrasse', label: 'Terrasse' },
  { value: 'salle', label: 'Salle' },
  { value: 'ambiance', label: 'Ambiance' },
];

export default function PhotoManagement() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('PHOTOS', 'edit');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editPhoto, setEditPhoto] = useState(null);
  const [form, setForm] = useState({ url: '', alt: '', category: 'plats', file: null });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const res = await adminApi.getPhotos();
      setPhotos(res.data || []);
    } catch { toast.error('Erreur chargement photos'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    setUploading(true);
    try {
      let imageUrl = form.url;
      if (form.file) {
        const fd = new FormData();
        fd.append('file', form.file);
        const up = await adminApi.uploadPhoto(fd);
        imageUrl = up.data.url;
      }
      if (!imageUrl) { toast.error('URL ou fichier requis'); setUploading(false); return; }
      await adminApi.createPhoto({ url: imageUrl, alt: form.alt, category: form.category, order: photos.length });
      toast.success('Photo ajoutée');
      setShowDialog(false);
      setForm({ url: '', alt: '', category: 'plats', file: null });
      load();
    } catch { toast.error('Erreur ajout photo'); }
    setUploading(false);
  };

  const handleEdit = async () => {
    if (!editPhoto) return;
    try {
      await adminApi.updatePhoto(editPhoto.id, { alt: editPhoto.alt, category: editPhoto.category });
      toast.success('Photo modifiée');
      setShowEditDialog(false);
      setEditPhoto(null);
      load();
    } catch { toast.error('Erreur modification'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await adminApi.deletePhoto(id);
      toast.success('Photo supprimée');
      load();
    } catch { toast.error('Erreur suppression'); }
  };

  const movePhoto = async (photo, direction) => {
    const sorted = [...photos].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(p => p.id === photo.id);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= sorted.length) return;
    
    // Swap orders
    const reordered = sorted.map((p, i) => ({ ...p }));
    const temp = reordered[idx].order;
    reordered[idx].order = reordered[newIdx].order;
    reordered[newIdx].order = temp;
    
    // Optimistic update
    setPhotos(reordered);

    try {
      await adminApi.reorderPhotos(reordered.map(p => ({ id: p.id, order: p.order })));
    } catch { 
      toast.error('Erreur réordonnement');
      load();
    }
  };

  const openEdit = (photo) => {
    setEditPhoto({ ...photo });
    setShowEditDialog(true);
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center py-20"><div className="w-10 h-10 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-heading font-bold text-garden-800 mb-6" data-testid="photos-title">Photos</h1>
        
        <div className="flex justify-between items-center mb-6">
          <span className="text-sm text-charcoal/60">{photos.length} photos</span>
          {canEdit && (
          <Button onClick={() => setShowDialog(true)} data-testid="admin-add-photo-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">
            <Plus size={16} className="mr-1" /> Ajouter une photo
          </Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...photos].sort((a, b) => a.order - b.order).map((photo) => (
            <div key={photo.id} className="group relative rounded-xl overflow-hidden border border-garden-200 bg-white" data-testid={`photo-card-${photo.id}`}>
              <img src={photo.url} alt={photo.alt} className="w-full aspect-square object-cover" />
              <div className="p-2 text-xs text-charcoal/70 truncate">{photo.alt || 'Sans description'}</div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                {canEdit && (
                <>
                <button onClick={() => openEdit(photo)} className="bg-white/90 rounded-full p-1.5 shadow hover:bg-white" data-testid={`edit-photo-${photo.id}`}><Pencil size={14} /></button>
                <button onClick={() => movePhoto(photo, -1)} className="bg-white/90 rounded-full p-1.5 shadow hover:bg-white"><ArrowUp size={14} /></button>
                <button onClick={() => movePhoto(photo, 1)} className="bg-white/90 rounded-full p-1.5 shadow hover:bg-white"><ArrowDown size={14} /></button>
                <button onClick={() => handleDelete(photo.id)} className="bg-red-500/90 text-white rounded-full p-1.5 shadow hover:bg-red-600"><Trash2 size={14} /></button>
                </>
                )}
              </div>
              <div className="absolute top-2 left-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/80 text-charcoal/70 font-medium">
                  {photoCategories.find(c => c.value === photo.category)?.label || photo.category}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Dialog ajout */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Ajouter une photo</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Image (upload)</Label>
                <input type="file" accept="image/*" onChange={e => setForm({ ...form, file: e.target.files[0] })} className="text-sm w-full" data-testid="photo-file-input" />
              </div>
              <div>
                <Label className="text-sm">Ou URL de l'image</Label>
                <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} className="border-garden-200 rounded-xl" placeholder="https://..." data-testid="photo-url-input" />
              </div>
              <div>
                <Label className="text-sm">Description (texte alt)</Label>
                <Input value={form.alt} onChange={e => setForm({ ...form, alt: e.target.value })} className="border-garden-200 rounded-xl" placeholder="Terrasse ombragée..." data-testid="photo-alt-input" />
              </div>
              <div>
                <Label className="text-sm">Catégorie</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="border-garden-200 rounded-xl" data-testid="photo-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {photoCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">Annuler</Button>
              <Button onClick={handleAdd} disabled={uploading} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="photo-save-btn">
                {uploading ? 'Upload...' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog édition */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifier la photo</DialogTitle></DialogHeader>
            {editPhoto && (
            <div className="space-y-4">
              <img src={editPhoto.url} alt={editPhoto.alt} className="w-full max-h-48 object-contain rounded-lg border" />
              <div>
                <Label className="text-sm">Description (texte alt)</Label>
                <Input value={editPhoto.alt || ''} onChange={e => setEditPhoto({ ...editPhoto, alt: e.target.value })} className="border-garden-200 rounded-xl" data-testid="edit-photo-alt" />
              </div>
              <div>
                <Label className="text-sm">Catégorie</Label>
                <Select value={editPhoto.category || 'plats'} onValueChange={v => setEditPhoto({ ...editPhoto, category: v })}>
                  <SelectTrigger className="border-garden-200 rounded-xl" data-testid="edit-photo-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {photoCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)} className="rounded-full">Annuler</Button>
              <Button onClick={handleEdit} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="edit-photo-save-btn">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
